/**
 * MultiSelectDropdown — checkable multi-select built on the dropdown-menu
 * primitive (no shadcn Select: it's single-value only).
 *
 * `selected` is the set of chosen values; an empty set means "no filter" (All…).
 * One click per item toggles it; a "Clear" shortcut appears when anything is
 * selected; "Done" hints that the menu stays open while picking.
 */
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidersHorizontal, Check } from 'lucide-react';

export function MultiSelectDropdown({
  placeholder,
  options,
  selected,
  onChange,
  className,
}: {
  placeholder: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={`h-8 text-xs flex items-center justify-between gap-1 bg-white text-gray-900 border border-gray-300 rounded-md px-3 ${
          selected.length > 0 ? 'border-blue-300 text-blue-700' : ''
        } ${className ?? ''}`}
      >
        <span className="truncate">{summary}</span>
        <SlidersHorizontal className="h-3 w-3 text-gray-400 flex-shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-white text-gray-900 border-gray-200 shadow-lg max-h-96 overflow-y-auto min-w-[220px]"
      >
        {selected.length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center justify-between pr-2 text-xs font-semibold text-gray-500">
              {placeholder}
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  setOpen(false);
                }}
                className="text-[11px] font-medium text-blue-600 hover:underline"
              >
                Clear
              </button>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-200" />
          </>
        )}
        {options.map((o) => {
          const checked = selected.includes(o);
          return (
            <DropdownMenuItem
              key={o}
              onSelect={(e) => {
                e.preventDefault();
                toggle(o);
              }}
              className={`flex items-center gap-2 text-sm focus:bg-blue-50 focus:text-blue-900 ${
                checked ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
              }`}
            >
              <span
                className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                  checked ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                }`}
              >
                {checked && <Check className="h-3 w-3 text-white" />}
              </span>
              <span className="truncate">{o}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-gray-200" />
        <DropdownMenuLabel className="text-[11px] text-gray-400 font-normal text-center">
          Done
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
